import React, {
  ComponentProps,
  FC,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { datadogRum } from '@datadog/browser-rum';
import type { LDEvaluationDetail } from 'launchdarkly-js-client-sdk';

import { ENVS, getEnv, getRegion } from '@adsk/acs-sc-web-platform-api';
import { createLaunchDarklyContext } from '@adsk/alloy-provider';
import { noop } from '@adsk/alloy-react-helpers';

const LD_API_KEYS = {
  [ENVS.QA]: '5e4c38359d07d807cfb6fc26',
  [ENVS.STAGING]: '5e4c3835aadaaf0815842d0e',
  [ENVS.PRODUCTION]: '5e4c3834d497f707dd287f20',
};

export const DEFAULT_ANONYMOUS_USER_ID = 'nonuser' as const;

const { SimpleLaunchDarklyProvider, useFeatureFlag, useAllFeatureFlags } =
  createLaunchDarklyContext();

type LDUser = {
  analyticsId?: string | null;
  email?: string;
};

type AlloyLaunchDarklyContextValue = {
  projectId?: string;
  accountId?: string;
  user?: LDUser;
  setUser: (user?: LDUser) => void;
  setProjectId: (projectId?: string) => void;
  setAccountId: (accountId?: string) => void;
};

const LaunchDarklyUserContext = createContext<AlloyLaunchDarklyContextValue>({
  setUser: noop,
  setProjectId: noop,
  setAccountId: noop,
  user: undefined,
});

const useLaunchDarklyUser = (
  user: LDUser | undefined,
  region: string,
  accountId?: string,
  projectId?: string,
) => {
  const { analyticsId, email } = user || {};

  return useMemo(() => {
    const custom = { region };
    if (accountId) {
      Object.assign(custom, { accountId });
    }
    if (projectId) {
      Object.assign(custom, { projectId });
    }
    return {
      key: analyticsId || DEFAULT_ANONYMOUS_USER_ID,
      anonymous: true,
      email,
      custom,
    };
  }, [email, analyticsId, region, accountId, projectId]);
};

const useLaunchDarklyData = () => {
  return useContext(LaunchDarklyUserContext);
};

type AlloyLaunchDarklyProviderProps = Omit<
  ComponentProps<typeof SimpleLaunchDarklyProvider>,
  'apiKey'
>;

const AllFeatureFlagsSubscriber: FC = () => {
  const { loading, value: featureFlags } = useAllFeatureFlags();

  useEffect(() => {
    if (!loading && featureFlags) {
      for (const [key, value] of Object.entries(featureFlags)) {
        datadogRum.addFeatureFlagEvaluation(key, value);
      }
    }
  }, [featureFlags, loading]);

  return null;
};

const LaunchDarklyProvider: FC<AlloyLaunchDarklyProviderProps> = (props) => {
  const env = getEnv();
  const region = getRegion();
  const apiKey = useMemo(() => LD_API_KEYS[env], [env]);
  const [user, setUser] = useState<LDUser | undefined>(undefined);
  const [accountId, setAccountId] = useState<string | undefined>(undefined);
  const [projectId, setProjectId] = useState<string | undefined>(undefined);
  const ldUser = useLaunchDarklyUser(user, region, accountId, projectId);

  return (
    <LaunchDarklyUserContext.Provider
      value={{
        projectId,
        accountId,
        user,
        setUser,
        setProjectId,
        setAccountId,
      }}
    >
      <SimpleLaunchDarklyProvider
        user={ldUser}
        options={{
          allAttributesPrivate: true,
          sendEventsOnlyForVariation: true,
          inspectors: [
            {
              type: 'flag-used',
              name: 'monitoring-inspector',
              method: (key: string, detail: LDEvaluationDetail) => {
                datadogRum.addFeatureFlagEvaluation(key, detail.value);
              },
            },
          ],
        }}
        apiKey={apiKey}
        {...props}
      >
        <AllFeatureFlagsSubscriber />
        {props.children}
      </SimpleLaunchDarklyProvider>
    </LaunchDarklyUserContext.Provider>
  );
};

export { LaunchDarklyProvider, useFeatureFlag, useLaunchDarklyData };
